import { useEffect, useMemo, useRef, useState } from "react";
import { MapContext } from "./core/MapContext";
import { createMapAdapter } from "./core/MapFactory";
import { PluginManager } from "./plugins/PluginManager";
import type { MapType, UniversalMapProps } from "./types/MapTypes";

import { useDispatch, useSelector } from "react-redux";
import { AOIPlugin } from "./plugins/aoi";
import { DrawPlugin } from "./plugins/draw";
import { MeasurePlugin } from "./plugins/measure";
import { PluginLoader } from "./plugins/PluginLoader";

import { RootState } from "../../../store/store";
import LayerPanel from "./layers/components/LayerPanel";

import MapControls from "./MapControls";
import MapTypeSelector from "./MapTypeSelector";
import AOIToolbar from "./plugins/aoi/components/AOIToolbar";
import DrawToolbar from "./plugins/draw/components/DrawToolbar";
import MeasureToolbar from "./plugins/measure/components/MeasureToolbar";
import { setDrawingAOIMode } from "./store/slices/aoiSlice";
import
{
  moveLayerDown,
  moveLayerUp,
  removeLayer,
  setOpacity,
  toggleVisible,
} from "./store/slices/layerSlice";
import
{
  changeBasemap,
  changeMapType,
} from "./store/slices/mapSlice";
import { loadBasemaps } from "./store/thunks/loadBasemaps";
import { LocalStorageKey } from "../../../constants/localStorageKeys";
import { ENGINE_REGISTRY } from "./utils/consts";
import SearchOSM from "./plugins/searchOSM";

export function UniversalMap({
  mapType,
  view,
  style,
  className,
  children,
  showEngine,
  showDraw,
  showDrawAOI,
  onMapReady,
  showMeasure,
  showBaseMap,
  showMapControl,
  showSearchOSM,
  allowMoveActive,
  supportedAOITypes,
  aoiAreaLimits
}: UniversalMapProps)
{
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<any>(null);
  const pluginManager = useMemo(() => new PluginManager(), []);

  const dispatch = useDispatch();

  const { layers } = useSelector((state: RootState) => state.layer);

  const {
    basemaps,
    selectedBasemap,
  } = useSelector((state: RootState) => state.map);

  const {
    view: mapView,
    mousePosition,
  } = useSelector((state: RootState) => state.mapBase);

  const [initialized, setInitialized] = useState<boolean>(false);

  // Track the basemap id currently applied to this engine so we don't re-apply
  // (and flicker) when selectedBasemap's object reference changes but the id doesn't.
  const appliedBasemapIdRef = useRef<string | undefined>(undefined);

  useEffect(() =>
  {
    // @ts-ignore – thunk
    dispatch(loadBasemaps());
  }, []);

  useEffect(() =>
  {
    if (!containerRef.current) return;

    const engine = createMapAdapter(mapType);
    engine.init(containerRef.current);

    engine.syncLayers(layers || []);
    engine.setBasemap(selectedBasemap);
    appliedBasemapIdRef.current = selectedBasemap?.id;

    engineRef.current = engine;

    if (view) engine.setView(view);

    onMapReady?.(engine);

    return () =>
    {
      const mapInstance = engineRef.current
        ? mapType === "cesium"
          ? engineRef.current?.viewer
          : engineRef.current?.map
        : null;

      pluginManager.clear({
        mapType,
        mapInstance,
        dispatch: () => { },
        getState: () => ({}),
      });

      engine.destroy();
    };
  }, [mapType]);

  useEffect(() =>
  {
    if (!containerRef.current) return;
    setTimeout(() =>
    {
      setInitialized(true);
    }, 10);
  }, [engineRef.current]);

  useEffect(() =>
  {
    if (engineRef.current) engineRef.current?.syncLayers(layers);
  }, [layers]);

  useEffect(() =>
  {
    if (!engineRef.current || !selectedBasemap) return;
    // Skip if the same basemap is already applied (avoids restyle/flicker on
    // redundant reference changes, e.g. another map mounting).
    if (appliedBasemapIdRef.current === selectedBasemap.id) return;
    appliedBasemapIdRef.current = selectedBasemap.id;
    engineRef.current.setBasemap(selectedBasemap);
  }, [selectedBasemap]);

  //! functions

  const onAdd = () => { };
  const onRemove = (id: string) =>
  {
    dispatch(removeLayer(id));
  };
  const onToggle = (id: string) =>
  {
    dispatch(toggleVisible(id));
  };
  const onOpacity = (id: string, opacity: number) =>
  {
    dispatch(setOpacity({ id, opacity }));
  };
  const onMoveUp = (id: string) =>
  {
    dispatch(moveLayerUp(id));
  };
  const onMoveDown = (id: string) =>
  {
    dispatch(moveLayerDown(id));
  };

  const onSetBasemap = (id: string) =>
  {
    const found = basemaps.find((b) => b.id === id);
    dispatch(changeBasemap(found));
    // Persist the selection so a reload restores this basemap.
    if (found)
    {
      localStorage.setItem(
        LocalStorageKey.BASEMAP_SELECTED,
        JSON.stringify(found),
      );
    }
  };

  const handleMapTypeChange = (type: MapType) =>
  {
    dispatch(changeMapType(type));
  };

  const onZoomIn = () =>
  {
    switch (mapType)
    {
      case "maplibre":
        engineRef.current?.map?.setZoom(Math.min(mapView.zoom + 1, 22));
        break;
    }
  };
  const onZoomOut = () =>
  {
    switch (mapType)
    {
      case "maplibre":
        engineRef.current?.map?.setZoom(Math.max(mapView.zoom - 1, 0));
        break;
    }
  };
  const onResetNorth = () =>
  {
    switch (mapType)
    {
      case "maplibre":
        {
          const map = engineRef.current?.map;
          if (!map) return;
          // Reset bearing + pitch to 0 (north up) on the LIVE map, keeping the
          // current center/zoom. Don't rely on redux mapView (it doesn't track
          // bearing and can be stale).
          if (typeof map.resetNorthPitch === "function")
          {
            map.resetNorthPitch({ duration: 300 });
          } else
          {
            map.easeTo({ bearing: 0, pitch: 0, duration: 300 });
          }
        }
        break;
    }
  };

  return (
    <MapContext.Provider
      value={{
        engine: engineRef.current,
        pluginManager,
        mapType,
        mapInstance: engineRef.current
          ? mapType === "cesium"
            ? engineRef.current.viewer
            : engineRef.current.map
          : null,
      }}
    >
      {initialized && (
        <>
          {children}
          <PluginLoader plugin={MeasurePlugin} />

          {showSearchOSM && (
            <SearchOSM
              map={
                engineRef.current
                  ? mapType === "cesium"
                    ? engineRef.current.viewer
                    : engineRef.current.map
                  : null
              }
            />
          )}

          {showMeasure && (
            <>
              <MeasureToolbar />
            </>
          )}
          <PluginLoader
            plugin={AOIPlugin}
            allowMoveActive={allowMoveActive}
            aoiAreaLimits={aoiAreaLimits}
          />
          {showDrawAOI && (
            <>
              <AOIToolbar
                supportedAOITypes={supportedAOITypes}
                aoiAreaLimits={aoiAreaLimits}
              />
            </>
          )}
          <PluginLoader plugin={DrawPlugin} />
          {showDraw && (
            <>
              <DrawToolbar />
            </>
          )}

          {showBaseMap && (
            <LayerPanel
              layers={layers}
              onAdd={onAdd}
              onRemove={onRemove}
              onToggle={onToggle}
              onOpacity={onOpacity}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onSetBasemap={onSetBasemap}
            />
          )}
          {showMapControl && (
            <MapControls
              viewport={mapView}
              mousePosition={mousePosition}
              onZoomIn={onZoomIn}
              onZoomOut={onZoomOut}
              onResetNorth={onResetNorth}
            />
          )}

          {showEngine && (
            <MapTypeSelector
              current={mapType}
              engines={ENGINE_REGISTRY}
              onChange={handleMapTypeChange}
            />
          )}
        </>
      )}
      <div
        ref={containerRef}
        className={className}
        style={{ width: "100%", height: "100%", ...style }}
      />
    </MapContext.Provider>
  );
}
